import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { alertService } from "client-app-core";
import { SvgIcon, Menu, MenuItem, Drawer } from "@mui/material";
import { mdiCog } from "@mdi/js";
import { makeStyles } from "@mui/styles";
import ChevronRight from "@mui/icons-material/ChevronRight";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import {
	acknowledgeAlerts,
	closeBulkAlerts,
	dumpArchive,
	getArchiveFailed,
	getArchiveSuccess
} from "orion-components/Dock/Alerts/actions";
import EventBus from "orion-components/SharedComponents/EventBus";
import { getDir } from "orion-components/i18n/Config/selector";
import { Translate } from "orion-components/i18n";
import { DockRightSingleColumn, DockRightDoubleColumn, DockBottom } from "orion-components/CBComponents/Icons";
import ArchivedNotifications from "./ArchivedNotifications";
import { acknowledgeNotifications, closeBulkNotifications, setDockOptionSettings } from "../actions";
import { setLocalAppState } from "orion-components/AppState/Actions";
import { selectIsAlertConcurrenceEnabled } from "../../../GlobalData/Selectors";
import { getEcoSettingByKey } from "orion-components/Services/EcoSettingService/selectors";

const ANIMATION_DURATION = 300;
const FETCH_THROTTLE_DURATION = 10000;
const DISMISS_DELAY = 400;

const useMenuStyles = makeStyles({
	paper: {
		boxShadow: "rgb(0,0,0,0.7) -5px 5px 10px 0px",
		backgroundColor: "#494d53"
	},
	menu: {
		paddingTop: 0,
		paddingBottom: 0
	}
});

const SettingsMenu = ({
	notifications,
	forReplay,
	setAnimating,
	bottomAlertListEnabled,
	customStyles = {},
	setShouldDisplayLoading,
	disableDockOptions
}) => {
	const dispatch = useDispatch();
	const menuClasses = useMenuStyles();

	// Redux selectors
	const { alertPanelLayout, alertPanelDockedBottom } = useSelector(state => state.appState?.persisted) || {};
	const dir = useSelector(state => getDir(state));
	const isAlertConcurrenceEnabled = useSelector(selectIsAlertConcurrenceEnabled);
	const disableAlertArchive = useSelector(state => getEcoSettingByKey("disableAlertArchive")(state) || false);
	const disableAlertAckAll = useSelector(state => getEcoSettingByKey("disableAlertAckAll")(state) || false);

	// State
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
	const [isSwapping, setIsSwapping] = useState(false);
	const [isArchiveOpen, setIsArchiveOpen] = useState(false);
	const [shouldFetchNextPage, setShouldFetchNextPage] = useState(true);
	const [shouldAllowFetch, setShouldAllowFetch] = useState(true);
	const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);

	// Refs
	const settingMenuRef = useRef(null);
	const menuItemRef = useRef(null);
	const loadedPages = useRef(0);
	const loadingTimeoutRef = useRef(null);

	// Memoized values
	const archivedNotifications = useMemo(
		() => notifications?.filter(item => (forReplay ? item.transactionClosed : item.closed)) || [],
		[notifications, forReplay]
	);

	const activeNotifications = useMemo(
		() => notifications?.filter(n => (forReplay ? !n.transactionClosed : !n.closed)) || [],
		[notifications, forReplay]
	);

	const allOptionsDisabled =
		disableAlertAckAll && isAlertConcurrenceEnabled && disableAlertArchive && disableDockOptions;

	// Handlers
	const handleMenuOpen = useCallback(() => {
		setIsMenuOpen(true);
	}, []);

	const handleMenuClose = useCallback(() => {
		setIsMenuOpen(false);
	}, []);

	const scrollToTop = useCallback(() => {
		const notificationTabWrapper = document.getElementById("notification-tab-wrapper");
		if (notificationTabWrapper) {
			notificationTabWrapper.scrollTop = 0;
		}
	}, []);

	const handleDockOptionClick = useCallback(
		dockOption => {
			const update = {};
			if (dockOption === "dockBottom") {
				update.alertPanelDockedBottom = true;
				dispatch(setLocalAppState("detailView", false));
			} else {
				update.alertPanelLayout = dockOption;
				if (bottomAlertListEnabled) {
					update.alertPanelDockedBottom = false;
				}
			}

			dispatch(setDockOptionSettings(update));
			setIsSubMenuOpen(false);
			setIsMenuOpen(false);
		},
		[dispatch, bottomAlertListEnabled]
	);

	const handleAckClick = useCallback(() => {
		const notificationsWithPriority = activeNotifications.filter(
			notification => notification?.eventPriorityId && notification.occurrenceCount !== notification.ackedCount
		);
		const notificationsWithoutPriority = activeNotifications.filter(
			notification => !notification?.eventPriorityId && notification.occurrenceCount !== notification.ackedCount
		);

		if (notificationsWithPriority.length > 0) {
			const priorityAlertPayload = notificationsWithPriority.map(n => ({
				alertId: n.id,
				occurrenceId: "*"
			}));
			dispatch(acknowledgeAlerts(priorityAlertPayload));
			EventBus.publish("getAcknowledgedAlerts", {
				alertsData: notificationsWithPriority.map(n => n.id),
				ackAll: true
			});
		}

		if (notificationsWithoutPriority.length > 0) {
			const nonPriorityAlertPayload = notificationsWithoutPriority.map(n => ({
				alertId: n.id,
				occurrenceId: "*"
			}));
			dispatch(acknowledgeNotifications(nonPriorityAlertPayload));
		}

		handleMenuClose();
	}, [activeNotifications, dispatch, handleMenuClose]);

	const handleDismissClick = useCallback(() => {
		const notificationIds = activeNotifications.map(n => n.id);
		EventBus.publish("closeAlerts", notificationIds);

		// If a notification has an eventPriorityId it is classified as an alert
		const notificationsWithPriority = activeNotifications.filter(notification => notification?.eventPriorityId);
		const notificationsWithoutPriority = activeNotifications.filter(notification => !notification?.eventPriorityId);

		if (notificationsWithPriority.length > 0) {
			setTimeout(() => dispatch(closeBulkAlerts(notificationsWithPriority, "alertService")), DISMISS_DELAY);
		}

		if (notificationsWithoutPriority.length > 0) {
			const nonPriorityNotificationIds = notificationsWithoutPriority.map(n => n.id);
			setTimeout(
				() => dispatch(closeBulkNotifications(nonPriorityNotificationIds, "notificationService")),
				DISMISS_DELAY
			);
		}

		if (setAnimating) {
			setAnimating(true);
		}
		handleMenuClose();
		dispatch(setLocalAppState("detailView", false));
	}, [activeNotifications, dispatch, setAnimating, handleMenuClose]);

	const handleArchiveClick = useCallback(() => {
		if (isSwapping) {
			return;
		}

		setIsSwapping(true);
		scrollToTop();
		setTimeout(() => setIsSwapping(false), ANIMATION_DURATION);
		setIsArchiveOpen(true);
		handleMenuClose();
	}, [isSwapping, scrollToTop, handleMenuClose]);

	const closeArchive = useCallback(() => {
		setIsArchiveOpen(false);
	}, []);

	const fetchNextPageConditionally = useCallback(() => {
		const nextPage = shouldFetchNextPage ? loadedPages.current + 1 : loadedPages.current;

		if (!shouldFetchNextPage) {
			// If we've reopened any already-loaded notifications, we need to refresh the loaded pages
			// before getting new ones so we don't lose items
			setShouldFetchNextPage(true);
		}

		setIsFetchingNextPage(true);

		alertService.getArchivedByPage(nextPage, (err, response) => {
			if (err) {
				console.error("Error fetching archived notifications:", err);
				dispatch(getArchiveFailed());
				return;
			}

			const currentArchivedNotifications =
				notifications?.filter(item => (forReplay ? item.transactionClosed : item.closed)) || [];

			if (response.length === currentArchivedNotifications.length) {
				// If the response has the same items, we want to disable the scroll call for X seconds
				// to avoid excess requests
				setShouldAllowFetch(false);
				setTimeout(() => setShouldAllowFetch(true), FETCH_THROTTLE_DURATION);
			}

			dispatch(getArchiveSuccess(response));

			if (loadingTimeoutRef.current) {
				clearTimeout(loadingTimeoutRef.current);
				loadingTimeoutRef.current = null;
			}

			loadedPages.current = nextPage;

			if (setShouldDisplayLoading) {
				setShouldDisplayLoading(false);
			}

			setIsFetchingNextPage(false);
		});
	}, [shouldFetchNextPage, notifications, forReplay, dispatch, setShouldDisplayLoading]);

	// Effects
	useEffect(() => {
		if (isArchiveOpen) {
			fetchNextPageConditionally();
		} else {
			// If we are switching to "Active"
			setTimeout(() => dispatch(dumpArchive()), ANIMATION_DURATION);
			// Remove all loaded archive notifications; switch back to archive means we're starting fresh at page 1
			loadedPages.current = 0;
			setShouldFetchNextPage(true);
			setShouldAllowFetch(true);

			if (setShouldDisplayLoading) {
				setShouldDisplayLoading(false);
			}
		}
	}, [isArchiveOpen, dispatch, setShouldDisplayLoading]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (loadingTimeoutRef.current) {
				clearTimeout(loadingTimeoutRef.current);
			}
		};
	}, []);

	// Styles
	const styles = useMemo(
		() => ({
			menuList: {
				padding: 0
			},
			menuItem: {
				height: "44px",
				backgroundColor: "#41454B !important",
				color: "#fff",
				padding: "15px 19px",
				fontSize: 12,
				"&:hover": {
					backgroundColor: "#2D2E2F !important"
				}
			},
			subMenuItem: {
				fontSize: 10,
				lineHeight: "13px"
			},
			dockMenuIcons: {
				width: 14,
				height: 11,
				...(dir === "rtl" ? { marginLeft: 5 } : { marginRight: 5 })
			},
			settingsIcon: {
				width: 20,
				height: 20,
				color: "#979798",
				cursor: "pointer",
				margin: "10px 5px -10px",
				...customStyles
			},
			chevronIcon: {
				fontSize: "18px",
				color: "#fff",
				...(dir === "rtl" ? { marginRight: 20 } : { marginLeft: 20 })
			}
		}),
		[dir, customStyles]
	);

	const isDockOptionSelected = useCallback(
		option => {
			if (bottomAlertListEnabled) {
				if (option === "dockBottom") {
					return alertPanelDockedBottom;
				}
				return (
					!alertPanelDockedBottom &&
					(alertPanelLayout === option || (!alertPanelLayout && option === "one-column"))
				);
			}
			return alertPanelLayout === option || (!alertPanelLayout && option === "one-column");
		},
		[bottomAlertListEnabled, alertPanelDockedBottom, alertPanelLayout]
	);

	return (
		<>
			<SvgIcon style={styles.settingsIcon} onClick={handleMenuOpen} ref={settingMenuRef}>
				<path d={mdiCog} />
			</SvgIcon>
			<Menu
				id="basic-menu"
				anchorEl={settingMenuRef.current}
				open={isMenuOpen}
				onClose={handleMenuClose}
				sx={{ zIndex: 2600 }}
				anchorOrigin={{
					horizontal: dir === "rtl" ? "left" : "right",
					vertical: "top"
				}}
				transformOrigin={{
					horizontal: dir === "rtl" ? "left" : "right",
					vertical: "top"
				}}
				PaperProps={{
					sx: {
						borderRadius: 0,
						"& ul": styles.menuList
					}
				}}
			>
				{allOptionsDisabled && (
					<MenuItem sx={styles.menuItem} disabled>
						<Translate value="global.dock.notifications.main.settingsMenu.noOptionsAvailable" />
					</MenuItem>
				)}
				{!disableAlertAckAll && (
					<MenuItem sx={styles.menuItem} onClick={handleAckClick}>
						<Translate value="global.dock.notifications.main.settingsMenu.acknowledgeAll" />
					</MenuItem>
				)}
				{!isAlertConcurrenceEnabled && (
					<MenuItem sx={styles.menuItem} onClick={handleDismissClick}>
						<Translate value="global.dock.notifications.main.settingsMenu.dismissAll" />
					</MenuItem>
				)}
				{!disableAlertArchive && (
					<MenuItem sx={styles.menuItem} onClick={handleArchiveClick}>
						<Translate value="global.dock.notifications.main.settingsMenu.viewArchive" />
					</MenuItem>
				)}
				{!disableDockOptions && (
					<div
						onMouseEnter={() => setIsSubMenuOpen(true)}
						onMouseLeave={() => setIsSubMenuOpen(false)}
						ref={menuItemRef}
					>
						<MenuItem sx={styles.menuItem}>
							<Translate value="global.dock.notifications.main.settingsMenu.dockOptions" />
							{dir === "rtl" ? (
								<ChevronLeft style={styles.chevronIcon} />
							) : (
								<ChevronRight style={styles.chevronIcon} />
							)}
						</MenuItem>
						<Menu
							style={{ pointerEvents: "none" }}
							anchorEl={menuItemRef.current}
							sx={{ zIndex: 2600 }}
							disablePortal
							anchorOrigin={{
								horizontal: dir === "rtl" ? "right" : "left",
								vertical: "top"
							}}
							transformOrigin={{
								horizontal: dir === "rtl" ? "left" : "right",
								vertical: "top"
							}}
							open={isSubMenuOpen}
							autoFocus={false}
							disableAutoFocus
							disableEnforceFocus
							onClose={() => setIsSubMenuOpen(false)}
							classes={{ paper: menuClasses.paper, list: menuClasses.menu }}
						>
							<div
								style={{
									pointerEvents: "auto",
									overflowY: "auto",
									maxHeight: "calc(100vh - 96px)"
								}}
							>
								<MenuItem
									className={isDockOptionSelected("one-column") ? "selectedDockOption" : ""}
									sx={{ ...styles.menuItem, ...styles.subMenuItem }}
									onClick={() => handleDockOptionClick("one-column")}
								>
									<DockRightSingleColumn style={styles.dockMenuIcons} />
									<Translate value="global.dock.notifications.main.settingsMenu.dockRightSingleColumn" />
								</MenuItem>
								<MenuItem
									className={isDockOptionSelected("two-column") ? "selectedDockOption" : ""}
									sx={{ ...styles.menuItem, ...styles.subMenuItem }}
									onClick={() => handleDockOptionClick("two-column")}
								>
									<DockRightDoubleColumn style={styles.dockMenuIcons} />
									<Translate value="global.dock.notifications.main.settingsMenu.dockRightDoubleColumn" />
								</MenuItem>
								{bottomAlertListEnabled && (
									<MenuItem
										className={isDockOptionSelected("dockBottom") ? "selectedDockOption" : ""}
										sx={{ ...styles.menuItem, ...styles.subMenuItem }}
										onClick={() => handleDockOptionClick("dockBottom")}
									>
										<DockBottom style={styles.dockMenuIcons} />
										<Translate value="global.dock.notifications.main.settingsMenu.dockBottom" />
									</MenuItem>
								)}
							</div>
						</Menu>
					</div>
				)}
			</Menu>
			<Drawer
				open={isArchiveOpen}
				anchor={dir === "rtl" ? "left" : "right"}
				sx={{
					zIndex: 2601,
					["& .MuiDrawer-paper"]: {
						width: "420px",
						backgroundColor: "#2C2B2D",
						position: "absolute !important",
						height: "calc(100vh - 48px) !important",
						top: "48px !important",
						overflow: "visible !important"
					}
				}}
			>
				<ArchivedNotifications
					notifications={archivedNotifications}
					closeArchive={closeArchive}
					archive={true}
					fetchNextPage={fetchNextPageConditionally}
					fetching={isFetchingNextPage}
					shouldFetch={shouldAllowFetch}
					dir={dir}
				/>
			</Drawer>
		</>
	);
};

export default memo(SettingsMenu);
